import { type ReactNode, useState } from 'react';

import { Link } from 'react-router-dom';

import { Icon } from './icon';
import { OnboardingFlow } from '../../../../backend/src/user/onboarding-flow';
import { useCompleteOnboardingFlow } from '@/api/mutations/useCompleteOnboardingFlow';
import helpImage from '@/assets/img/help.png';
import kimImage from '@/assets/img/kim.png';
import mattersImage from '@/assets/img/matters.png';
import subsetImage from '@/assets/img/subsets.png';
import uploadImage from '@/assets/img/upload-processing.png';
import { useUserContext } from '@/Contexts/User';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter } from '@/new/components/shadcn/dialog';

type OnboardingSlideDescription = ReactNode | ((onHelpCentreClick: () => void) => ReactNode);

export interface OnboardingSlide {
  imageSrc: string;
  title: string;
  description: OnboardingSlideDescription;
}

const slides: OnboardingSlide[] = [
  {
    imageSrc: mattersImage,
    title: 'Get started with Wexler',
    description: `Matters are where documents live in Wexler. To get started, create your own Matter or ask a team member to add you to an existing one.`,
  },
  {
    imageSrc: uploadImage,
    title: 'Upload and process documents',
    description: `In Wexler, once your documents have been uploaded, you need to submit them for processing. This is when facts, entities and inconsistencies are extracted, forming the Fact Intelligence that powers Kim.`,
  },
  {
    imageSrc: kimImage,
    title: 'Explore your Matter in Kim',
    description:
      'Ask Kim to retrieve key events, build chronologies, or draft documents. Every statement is sourced to an underlying fact, which you can view in the original document.',
  },
  {
    imageSrc: subsetImage,
    title: 'Group documents into Subsets',
    description:
      'Create Subsets to focus your research toward a select set of documents. Kim will only reference documents within the Subset, and work product lives separate from the wider Matter.',
  },
  {
    imageSrc: helpImage,
    title: 'Visit the Help Centre for more',
    description: (onHelpCentreClick) => (
      <>
        There's much more Wexler can do, so don't hesitate to visit our{' '}
        <Link to="/help-centre" className="text-blue-500 hover:underline" onClick={onHelpCentreClick}>
          Help Centre
        </Link>{' '}
        or reach out to{' '}
        <a href="mailto:support@wexler.ai" className="text-blue-500 hover:underline">
          support@wexler.ai
        </a>{' '}
        if you have any questions.
      </>
    ),
  },
];

export function FirstTimeOnboardingDialog() {
  const { user } = useUserContext();
  const { mutate: completeOnboardingFlow } = useCompleteOnboardingFlow();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);

  const shouldShow = !!user && !isDismissed && !user.completedOnboardingFlows.includes(OnboardingFlow.IntroDialog);
  const slide = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  const closeDialog = () => {
    setIsDismissed(true);
    completeOnboardingFlow(OnboardingFlow.IntroDialog);
  };
  const description = typeof slide.description === 'function' ? slide.description(closeDialog) : slide.description;

  return (
    <Dialog open={shouldShow} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="z-60 max-w-xl rounded-2xl p-1" overlayClassName="z-60" showCloseButton={false}>
        <div className="m-3 overflow-hidden rounded-lg border">
          <img src={slide.imageSrc} alt="" className="" />
        </div>

        <div className="p-2.5">
          <div className="h-22 space-y-1.5 px-4">
            <h2 className="text-2xl text-gray-800">{slide.title}</h2>
            <p className="text-gray-500">{description}</p>
          </div>

          <DialogFooter className="items-center justify-between! pr-1 pb-1 pl-4">
            <div
              className="mt-0.5 flex gap-1.5"
              role="img"
              aria-label={`Slide ${currentSlide + 1} of ${slides.length}`}
            >
              {slides.map((item, index) => (
                <span
                  key={item.title}
                  className={`size-1.5 rounded-full duration-150 ${index === currentSlide ? 'bg-gray-500' : 'bg-gray-300'}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                className={`transition-opacity ${currentSlide > 0 ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
                aria-hidden={currentSlide === 0}
                tabIndex={currentSlide === 0 ? -1 : 0}
                onClick={() => setCurrentSlide((index) => index - 1)}
              >
                <Icon className="lucide--arrow-left" />
                Back
              </Button>
              <Button
                className="pr-2"
                onClick={() => (isLastSlide ? closeDialog() : setCurrentSlide((index) => index + 1))}
              >
                {isLastSlide ? 'Get started' : 'Next'}
                <Icon className="lucide--chevron-right" />
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
